import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "util/request/request";
import { After10ApiError } from "util/request/types";
import { BenefitMainModel } from "models/benefitMainModel";
import { ApiPostCommonReplyParams, ModelList } from "types/Common";
import { ProductListModel } from "models/productListModel";
import { getReactQueryNextPageNum } from "util/common";
import { ApiPostBenefitViewLog, ApiPostCoupangViewLog } from "types/Benefit";
import { CommonReplyModel } from "models/commonReplyModel";
import { KidsGrowthShareEventListModel } from "models/kidsGrowthShareEventListModel";
import { MobileWebEventModel } from "models/mobileWebEventModel";
import { useAuthStore } from "stores/authStore";

const svcCd = "mobileweb_event";
const commonKeys = {
  all: ["common"] as const,
  reply: (svcId: number, page: number, pageSize?: number) =>
    [...commonKeys.all, "reply", svcId, page, pageSize] as const,
  mobileWebEvent: (randomKey: string) =>
    [...commonKeys.all, "mobileWebEvent", randomKey] as const,
  mobileWebEventReply: (eventId: number) =>
    [...commonKeys.all, "mobileWebEventReply", eventId] as const,
};

// 공통 댓글 조회
export const useGetCommonReply = (
  svcId: number,
  page: number,
  pageSize?: number,
) => {
  const token = useAuthStore((state) => state.token);
  return useQuery<ModelList<CommonReplyModel>, After10ApiError>({
    queryKey: commonKeys.reply(svcId, page, pageSize),
    queryFn: () =>
      getRequest({
        url: "/api/d/common/commonReply",
        params: {
          svcCd: "mobileweb_event",
          svcId,
          page,
          pageSize: pageSize ?? 3,
        },
      }),
    placeholderData: keepPreviousData,
  });
};

// 공통 댓글 저장
export const usePostCommonReply = () => {
  return useMutation({
    mutationFn: (params: ApiPostCommonReplyParams) =>
      postRequest({
        url: "/api/d/common/commonReply",
        params: { svcCd, ...params },
      }),
  });
};

// 공통 댓글 삭제
export const useDeleteCommonReply = () => {
  return useMutation({
    mutationFn: (replyId: number) => {
      return deleteRequest({ url: `/api/d/common/commonReply/${replyId}` });
    },
  });
};

export const useGetMobileWebEventReply = ({
  eventId,
  page,
  pageSize,
}: {
  eventId: number;
  page: number;
  pageSize: number;
}) => {
  const token = useAuthStore((state) => state.token);
  return useQuery<ModelList<CommonReplyModel>, After10ApiError>({
    queryKey: commonKeys.mobileWebEventReply(eventId),
    queryFn: () =>
      getRequest({
        url: `/api/d/common/commonReply`,
        params: { svcCd, svcId: `${eventId}`, page, pageSize },
      }),
  });
};

// 모바일 이벤트 정보
export const useGetMobileWebEvent = ({ randomKey }: { randomKey: string }) => {
  const token = useAuthStore((state) => state.token);
  return useQuery<MobileWebEventModel, After10ApiError>({
    queryKey: commonKeys.mobileWebEvent(randomKey),
    queryFn: () =>
      getRequest({ url: "/api/d/event/eventInfo", params: { randomKey } }),
  });
};

const benefitKeys = {
  all: ["benefit"] as const,
  main: ({
    osType,
    deviceId,
    sgroupCd,
  }: {
    osType: "1" | "2";
    deviceId?: string | null;
    sgroupCd?: string;
  }) => [...benefitKeys.all, "main", osType, deviceId, sgroupCd] as const,
  categoryProducts: ({
    categoryCd,
    orderTy,
  }: {
    categoryCd: string | number;
    orderTy?: string | number;
  }) => [...benefitKeys.all, "categoryProducts", categoryCd, orderTy],
};

export const useGetBenefitMain = (params: {
  token: string;
  deviceId?: string | null;
  sgroupCd?: string;
  osType?: "1" | "2";
}) => {
  const { osType, token } = params;

  return useQuery<BenefitMainModel, After10ApiError>({
    queryKey: benefitKeys.main({ ...params, osType: osType || "1" }),
    queryFn: () =>
      getRequest({
        url: "/api/d/benefit/main",
        params: { ...params },
      }),
  });
};

export const useGetBenefitCategoryProducts = (params: {
  categoryCd: string | number;
  orderTy?: string | number;
  enabled?: boolean;
}) => {
  const { categoryCd, orderTy, enabled } = params;
  const pageSize = 20;

  return useInfiniteQuery<ModelList<ProductListModel>, After10ApiError>({
    queryKey: benefitKeys.categoryProducts({ categoryCd, orderTy }),
    queryFn: ({ pageParam = 1 }) =>
      getRequest({
        url: "/api/d/benefit/coupang",
        params: { ...params, page: pageParam, pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      getReactQueryNextPageNum(lastPage, pageSize),
    enabled,
  });
};

// 혜택 뷰 로그
export const usePostBenefitViewLog = () => {
  return useMutation<any, After10ApiError, ApiPostBenefitViewLog>({
    mutationFn: (params) => {
      return postRequest({ url: "/api/d/benefit/benefitViewLog", params });
    },
  });
};

// 쿠팡 뷰 로그
export const usePostCoupangViewLog = () => {
  return useMutation<
    any,
    After10ApiError,
    ApiPostCoupangViewLog & { url?: string }
  >({
    mutationFn: ({
      url,
      ...params
    }: ApiPostCoupangViewLog & { url?: string }) => {
      return postRequest({ url: "/api/d/benefit/coupangViewLog", params });
    },
    onSuccess: (data, params) => {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "openExternalUrl", link: params.url }),
      );
    },
  });
};

const shareEventKeys = {
  all: ["shareEvent"] as const,
  shareEvent: (page: number, pageSize: number) =>
    [...shareEventKeys.all, "shareEvent", page, pageSize] as const,
};

// 아이 성장 SNS 공유 이벤트 리스트
export const useGetKidsGrowthShareEventList = (params: {
  page: number;
  pageSize: number;
}) => {
  const token = useAuthStore((state) => state.token);
  return useQuery<KidsGrowthShareEventListModel, After10ApiError>({
    queryKey: shareEventKeys.shareEvent(params.page, params.pageSize),
    queryFn: () =>
      getRequest({
        url: "/api/d/event/kidsGrowthShareEventList",
        params: { page: params.page, pageSize: params.pageSize },
      }),
    placeholderData: keepPreviousData,
  });
};

// 아이 성장 SNS 공유 이벤트 등록
export const usePostKidsGrowthShareEvent = () => {
  return useMutation<
    void,
    After10ApiError,
    {
      snsUrl1: string;
      snsUrl2?: string;
      snsUrl3?: string;
      snsUrl4?: string;
      snsUrl5?: string;
    }
  >({
    mutationFn: (params) =>
      postRequest({
        url: "/api/d/event/kidsGrowthShareEvent",
        params,
        isErrorAlert: false,
      }),
  });
};
